import React, { useEffect, useRef } from 'react';
import '../styles/CreateLeadModal.css';
import { PredictiveSearch, SearchInput } from '../components';

// Split REAPI's canonical address label into a 2-line display: street on
// top, city/state/zip below. Label format is typically
// "123 W Main St, Tampa, FL 33602". Fallback to single line if it doesn't
// match the expected pattern.
const splitCanonicalAddress = (label) => {
    if (typeof label !== 'string' || label.trim().length === 0) {
        return { line1: '', line2: '' };
    }
    const parts = label.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
        return { line1: parts[0], line2: parts.slice(1).join(', ') };
    }
    return { line1: label, line2: '' };
};

function CreateLeadModal(props) {
    const value = props.value;
    const onChange = props.onChange;
    const onSubmit = props.onSubmit;
    const onPick = props.onPick;
    // Fires synchronously the moment a paste is detected that will trigger
    // auto-submit. Lets the parent flip loading=true immediately so Continue
    // disables before the user can race-click it.
    const onPasteStart = typeof props.onPasteStart === 'function' ? props.onPasteStart : null;
    const results = props.results || [];
    const isSearching = props.isSearching === true;
    const loading = props.loading === true;
    const loadingMessage = props.loadingMessage || null;
    // Staged-progress checklist (paste flows). When non-empty, renders in
    // place of the single-message loading row. Each step is
    // { label, state: 'pending'|'active'|'complete', detail?: string }.
    const loadingSteps = Array.isArray(props.loadingSteps) ? props.loadingSteps : null;
    const error = props.error;
    // Preview state — when non-null, swap the inputs out for the preview card.
    const preview = props.preview || null;
    // Mode: 'address' (single-line input + autocomplete) or 'deal' (textarea
    // + paste-auto-submit). Two tabs let users self-select which UX fits
    // their input style — typing an address is a fundamentally different
    // task from pasting a wholesaler post, and one input compromised for
    // both is worse than two purpose-built ones.
    const mode = props.mode === 'deal' ? 'deal' : 'address';
    const setMode = typeof props.setMode === 'function' ? props.setMode : null;

    // Autocomplete dropdown only renders in address mode (deal mode never
    // needs it — paste auto-submits, typed prose goes through parse on
    // Continue). The !loading + !preview gates prevent a stale dropdown from
    // racing in-flight async work.
    const showAutocomplete = mode === 'address'
        && !loading
        && !preview
        && (results.length > 0 || isSearching === true);

    // Wrapper ref for SearchInput's container — SearchInput doesn't forward
    // its input ref, so we querySelector through the wrapper to auto-focus.
    // Keeps the shared SearchInput component contract untouched.
    const addressWrapperRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        // Auto-focus the active input on mode switch / open. Skip when
        // preview is up (preview is read-only confirmation) and when loading
        // (avoid stealing focus mid-async).
        if (loading || preview) return;
        if (mode === 'address' && addressWrapperRef.current) {
            const input = addressWrapperRef.current.querySelector('input');
            if (input) input.focus();
        } else if (mode === 'deal' && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [mode, loading, preview]);

    const handleAddressChange = (e) => {
        onChange(e.target.value);
    };

    const handleAddressClear = () => {
        onChange('');
    };

    const handleTextareaChange = (e) => {
        onChange(e.target.value);
    };

    const handleKeyDown = (e) => {
        // Cmd/Ctrl+Enter submits from the textarea.
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSubmit();
        }
    };

    // Paste-auto-submit ONLY in deal mode. Address mode lets default paste
    // happen and the autocomplete handles it via onChange. Keeps single-line
    // address pastes out of the parser path.
    //
    // We splice the pasted text in manually rather than letting the browser's
    // default action run, because onPasteStart synchronously flips the parent
    // to loading=true; React re-rendering the textarea as disabled before the
    // default paste lands silently swallows the input, leaving the textarea
    // visually empty while the parse path runs against a blank value.
    const handlePaste = (e) => {
        if (loading) return;
        const pasted = e.clipboardData?.getData('text') || '';
        if (pasted.trim().length === 0) return;
        e.preventDefault();
        const ta = textareaRef.current;
        const currentValue = typeof value === 'string' ? value : '';
        const selStart = ta?.selectionStart ?? currentValue.length;
        const selEnd = ta?.selectionEnd ?? currentValue.length;
        const merged = currentValue.slice(0, selStart) + pasted + currentValue.slice(selEnd);
        onChange(merged);
        if (onPasteStart) onPasteStart();
        const trimmed = merged.trim();
        if (trimmed.length === 0) return;
        onSubmit(trimmed);
    };

    // Preview card — shown after the parse path resolves and before
    // VerifyProperty opens.
    if (preview) {
        const { line1, line2 } = splitCanonicalAddress(preview.canonicalLabel);
        const senderName = preview.sender?.name || null;
        const senderPhone = preview.sender?.phone || null;
        const notesPreview = typeof preview.notes === 'string' && preview.notes.trim().length > 0
            ? (preview.notes.length > 120 ? `${preview.notes.slice(0, 117)}…` : preview.notes)
            : null;
        const bedsBaths = [];
        if (typeof preview.bedrooms === 'number') bedsBaths.push(`${preview.bedrooms} bed`);
        if (typeof preview.bathrooms === 'number') bedsBaths.push(`${preview.bathrooms} bath`);
        const bedsBathsLine = bedsBaths.join(' · ');

        return (
            <div className="create-lead-modal-body">
                <div className="create-lead-preview-card">
                    <span className="label-bold-caps colour-secondary create-lead-preview-eyebrow">
                        Found this property
                    </span>
                    <div className="create-lead-preview-address">
                        <span className="heading-small-bold colour-primary">
                            {line1}
                        </span>
                        {line2 && (
                            <span className="body-regular colour-secondary">
                                {line2}
                            </span>
                        )}
                    </div>
                    {(bedsBathsLine || senderName || senderPhone) && (
                        <div className="create-lead-preview-meta">
                            {bedsBathsLine && (
                                <span className="body-regular colour-secondary">
                                    {bedsBathsLine}
                                </span>
                            )}
                            {(senderName || senderPhone) && (
                                <span className="body-regular colour-secondary">
                                    {[senderName, senderPhone].filter(Boolean).join(' · ')}
                                </span>
                            )}
                        </div>
                    )}
                    {notesPreview && (
                        <div className="create-lead-preview-notes">
                            <span className="label-regular colour-secondary">From your text</span>
                            <span className="body-regular colour-primary">
                                "{notesPreview}"
                            </span>
                        </div>
                    )}
                </div>

                {error && (
                    <span className="body-regular colour-error create-lead-error">
                        {error}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className="create-lead-modal-body">
            <div className="create-lead-tabs" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={mode === 'address'}
                    className={`create-lead-tab body-regular${mode === 'address' ? ' create-lead-tab-active' : ''}`}
                    onClick={() => setMode && setMode('address')}
                    disabled={loading}
                >
                    Address
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={mode === 'deal'}
                    className={`create-lead-tab body-regular${mode === 'deal' ? ' create-lead-tab-active' : ''}`}
                    onClick={() => setMode && setMode('deal')}
                    disabled={loading}
                >
                    Paste deal
                </button>
            </div>

            {mode === 'address' ? (
                <div className="create-lead-address-container relative-container" ref={addressWrapperRef}>
                    <SearchInput
                        value={value}
                        onChange={handleAddressChange}
                        placeholder="123 Main St, Tampa, FL 33603"
                        type="text"
                        clearInput={handleAddressClear}
                        magnifyingGlassFunc={null}
                        searchLoading={isSearching}
                        disabled={loading}
                    />
                    {showAutocomplete && (
                        <PredictiveSearch
                            loading={isSearching}
                            results={results}
                            selectItem={onPick}
                            nested={false}
                        />
                    )}
                </div>
            ) : (
                <div className="create-lead-textarea-container relative-container">
                    <textarea
                        ref={textareaRef}
                        className="create-lead-textarea body-regular"
                        value={value}
                        onChange={handleTextareaChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        placeholder={`OFF MARKET — Phoenix, AZ
4827 N 29th Pl, Phoenix, AZ 85016
3 bed · 2 bath · 1,486 sqft · built 1962
Asking $310k cash, 10-day close
ARV $445k · rehab ~$55k (kitchen, bath, HVAC)
Tenant occupied, MTM, $1,650/mo
Photos: https://drive.google.com/drive/folders/1Xa9bC8dEfGh
Marcus · 480-555-0182`}
                        rows={9}
                        disabled={loading}
                    />
                </div>
            )}

            {loading && loadingSteps && loadingSteps.length > 0 && (
                <div className="create-lead-loading-steps">
                    {loadingSteps.map((step, idx) => (
                        <div
                            key={`${idx}-${step.label}`}
                            className="create-lead-loading-step"
                        >
                            <span className="create-lead-loading-step-icon" aria-hidden="true">
                                {step.state === 'active' && (
                                    <div className="create-lead-loading-spinner" />
                                )}
                                {step.state === 'complete' && (
                                    <span className="create-lead-loading-step-complete">
                                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                                            <path
                                                d="M3.5 8.5l3 3 6-7"
                                                stroke="currentColor"
                                                strokeWidth="1.75"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                fill="none"
                                            />
                                        </svg>
                                    </span>
                                )}
                                {step.state === 'pending' && (
                                    <span className="create-lead-loading-step-pending" />
                                )}
                            </span>
                            <span className={`body-regular ${step.state === 'pending' ? 'colour-secondary' : 'colour-primary'}`}>
                                {step.label}
                            </span>
                            {step.detail && step.state === 'complete' && (
                                <span className="label-regular create-lead-loading-step-detail">
                                    {step.detail}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {loading && !loadingSteps && loadingMessage && (
                <div className="create-lead-loading-row">
                    <div className="create-lead-loading-spinner" aria-hidden="true" />
                    <span className="body-regular colour-secondary">
                        {loadingMessage}
                    </span>
                </div>
            )}

            {error && (
                <span className="body-regular colour-error create-lead-error">
                    {error}
                </span>
            )}

            <span className="label-regular colour-secondary create-lead-hint">
                {mode === 'address'
                    ? "Pick a suggestion as you type, or hit Continue to use what's entered."
                    : "Pasting will auto-extract the property — no Continue click needed."}
            </span>
        </div>
    );
}

export default CreateLeadModal;
